import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getServices, generateTrace, generateRealtimeMetric } from "@/api/api";
import { Database, Play, Pause } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { Service } from "@/api/api";

interface DataVolumePoint {
  time: string;
  metrics: number;
  traces: number;
}

interface IngestionStats {
  [serviceName: string]: {
    metricsCollected: number;
    tracesCollected: number;
    lastIngestion: Date | null;
    isActive: boolean;
  };
}

const DataIngestion = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [ingestionStats, setIngestionStats] = useState<IngestionStats>({});
  const [autoGenerate, setAutoGenerate] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [dataVolume, setDataVolume] = useState<DataVolumePoint[]>([]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (autoGenerate) {
      interval = setInterval(() => {
        generateData();
      }, 5000); // Generate data every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoGenerate]);

  const fetchData = async () => {
    try {
      const servicesData = await getServices();
      setServices(servicesData);

      // Initialize stats
      const stats: IngestionStats = {};
      servicesData.forEach((service) => {
        stats[service.name] = {
          metricsCollected: 0,
          tracesCollected: 0,
          lastIngestion: null,
          isActive: true,
        };
      });
      setIngestionStats(stats);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const generateData = async () => {
    try {
      // Generate traces
      await generateTrace(false);

      // Generate metrics for random services
      const randomService = services[Math.floor(Math.random() * services.length)];
      if (randomService) {
        await generateRealtimeMetric(randomService.name, "avg_response_time");

        // Update stats
        setIngestionStats((prev) => ({
          ...prev,
          [randomService.name]: {
            ...(prev[randomService.name] || {
              metricsCollected: 0,
              tracesCollected: 0,
              lastIngestion: null,
              isActive: true,
            }),
            metricsCollected: (prev[randomService.name]?.metricsCollected || 0) + 1,
            tracesCollected: (prev[randomService.name]?.tracesCollected || 0) + 1,
            lastIngestion: new Date(),
          },
        }));

        // Update volume chart
        setDataVolume((prev) => {
          const newData = [
            ...prev,
            {
              time: new Date().toLocaleTimeString(),
              metrics: 1,
              traces: 1,
            },
          ];
          return newData.slice(-20); // Keep last 20 data points
        });
      }
    } catch (error) {
      console.error("Error generating data:", error);
    }
  };

  const toggleIngestion = (serviceName: string) => {
    setIngestionStats((prev) => ({
      ...prev,
      [serviceName]: {
        ...(prev[serviceName] || {
          metricsCollected: 0,
          tracesCollected: 0,
          lastIngestion: null,
          isActive: true,
        }),
        isActive: !prev[serviceName]?.isActive,
      },
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="data-ingestion-loading">
        <Database className="w-12 h-12 animate-pulse text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="data-ingestion-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Data Ingestion Pipeline</h2>
          <p className="text-gray-600 mt-1">Multi-cloud data collection and processing</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={autoGenerate ? "destructive" : "default"}
            onClick={() => setAutoGenerate(!autoGenerate)}
            data-testid="auto-generate-toggle"
          >
            {autoGenerate ? (
              <>
                <Pause className="w-4 h-4 mr-2" />
                Stop Auto-Generate
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Start Auto-Generate
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Real-time Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Metrics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {Object.values(ingestionStats).reduce((sum, s) => sum + (s.metricsCollected || 0), 0)}
            </div>
            <p className="text-xs text-gray-500 mt-1">Collected</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Traces</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {Object.values(ingestionStats).reduce((sum, s) => sum + (s.tracesCollected || 0), 0)}
            </div>
            <p className="text-xs text-gray-500 mt-1">Collected</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Active Sources</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {
                Object.values(ingestionStats).filter((s: IngestionStats[string]) => s.isActive)
                  .length
              }
            </div>
            <p className="text-xs text-gray-500 mt-1">Out of {services.length}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Ingestion Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{autoGenerate ? "5s" : "—"}</div>
            <p className="text-xs text-gray-500 mt-1">Collection interval</p>
          </CardContent>
        </Card>
      </div>

      {/* Data Volume Chart */}
      {dataVolume.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Real-Time Data Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dataVolume}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="metrics" stroke="#3b82f6" name="Metrics" />
                <Line type="monotone" dataKey="traces" stroke="#10b981" name="Traces" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Service Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>Data Source Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {services.map((service: Service) => {
              const stats = ingestionStats[service.name] || {
                metricsCollected: 0,
                tracesCollected: 0,
                lastIngestion: null,
                isActive: true,
              };
              return (
                <div
                  key={service.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  data-testid={`data-source-${service.name}`}
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <div
                      className={`w-3 h-3 rounded-full ${stats.isActive ? "bg-green-500 animate-pulse" : "bg-gray-400"}`}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{service.name}</span>
                        <Badge variant="outline">{service.cloud_provider}</Badge>
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {service.service_type.replace("_", " ")}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <div className="font-semibold text-blue-600">
                        {stats.metricsCollected || 0}
                      </div>
                      <div className="text-xs text-gray-500">Metrics</div>
                    </div>
                    <div className="text-center">
                      <div className="font-semibold text-green-600">
                        {stats.tracesCollected || 0}
                      </div>
                      <div className="text-xs text-gray-500">Traces</div>
                    </div>
                    {stats.lastIngestion && (
                      <div className="text-center">
                        <div className="text-xs text-gray-500">Last ingestion</div>
                        <div className="font-medium text-xs">
                          {new Date(stats.lastIngestion).toLocaleTimeString()}
                        </div>
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant={stats.isActive ? "destructive" : "default"}
                      onClick={() => toggleIngestion(service.name)}
                    >
                      {stats.isActive ? (
                        <Pause className="w-4 h-4" />
                      ) : (
                        <Play className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Configuration Options */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Collection Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <div className="font-medium text-sm">Metrics Collection</div>
                <div className="text-xs text-gray-500">
                  Response time, request count, error rate
                </div>
              </div>
              <Badge className="bg-green-100 text-green-800">Enabled</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <div className="font-medium text-sm">Distributed Tracing</div>
                <div className="text-xs text-gray-500">Service dependencies and spans</div>
              </div>
              <Badge className="bg-green-100 text-green-800">Enabled</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <div>
                <div className="font-medium text-sm">Log Collection</div>
                <div className="text-xs text-gray-500">Application and system logs</div>
              </div>
              <Badge variant="outline">Planned</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Data Quality Metrics</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Completeness</span>
              <div className="flex items-center gap-2">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div className="bg-green-500 h-2 rounded-full" style={{ width: "98%" }} />
                </div>
                <span className="text-sm font-medium">98%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Accuracy</span>
              <div className="flex items-center gap-2">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: "95%" }} />
                </div>
                <span className="text-sm font-medium">95%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Timeliness</span>
              <div className="flex items-center gap-2">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{ width: "99%" }} />
                </div>
                <span className="text-sm font-medium">99%</span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Consistency</span>
              <div className="flex items-center gap-2">
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div className="bg-orange-500 h-2 rounded-full" style={{ width: "97%" }} />
                </div>
                <span className="text-sm font-medium">97%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataIngestion;
